import {
  type PersonalProfile as PersonalProfileProto,
  PersonalInfo as PersonalInfoProto,
  type SpousalProfile as SpousalProfileProto,
  type DependentProfile as DependentProfileProto,
} from '@taxfyle/api-internal/internal/individual_profile_pb'
import type { PersonalProfileVersionProps } from '@/features/tax-profile/individual-profiles/PersonalProfile'
import type { SpousalProfileVersionProps } from '@/features/tax-profile/individual-profiles/SpousalProfile'
import type { DependentProfileVersionProps } from '@/features/tax-profile/individual-profiles/DependentProfile'
import { safeTimestampToDate } from '@/utils/grpc-util'
import { mapProtoToContactInfo } from '@/features/tax-profile/contact-info-mapper'
import { ContactInfo } from '@/features/tax-profile/ContactInfo'
import { PersonalInfo } from '@/features/tax-profile/individual-profiles/PersonalInfo'
import { dateFormatters, parseAmericanDate } from '@/utils/date-util'

export function mapPersonalProfileVersionProtoToProps(
  proto: PersonalProfileProto,
): PersonalProfileVersionProps {
  const personalInfoProto = proto.getPersonalInfo()
  const contactInfoProto = proto.getContactInfo()

  return {
    id: proto.getId(),
    version: proto.getVersion(),
    workspaceId: proto.getWorkspaceId(),
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    updatedAt: safeTimestampToDate(proto.getUpdateTime()),
    occupation: proto.getOccupation(),
    ownerUserId: proto.getOwnerUserId(),
    contactInfo: contactInfoProto
      ? mapProtoToContactInfo(contactInfoProto)
      : ContactInfo.empty,
    personalInfo: personalInfoProto
      ? mapPersonalInfoProtoToProps(personalInfoProto)
      : PersonalInfo.empty,
  }
}

export function mapPersonalInfoProtoToProps(
  proto: PersonalInfoProto,
): PersonalInfo {
  const dobStr = proto.getDateOfBirth()

  return {
    firstName: proto.getFirstName(),
    lastName: proto.getLastName(),
    middleInitial: proto.getMiddleInitial() || null,
    dateOfBirth: dobStr ? parseAmericanDate(dobStr) : null,
    personalTaxpayerIdentificationNumber:
      proto.getPersonalTaxpayerIdentificationNumber(),
  }
}

export function mapPersonalInfoToProto(
  personalInfo: PersonalInfo,
): PersonalInfoProto {
  const proto = new PersonalInfoProto()
    .setFirstName(personalInfo.firstName)
    .setLastName(personalInfo.lastName)
    .setPersonalTaxpayerIdentificationNumber(
      personalInfo.personalTaxpayerIdentificationNumber,
    )

  if (personalInfo.middleInitial) {
    proto.setMiddleInitial(personalInfo.middleInitial)
  }

  if (personalInfo.dateOfBirth !== null) {
    proto.setDateOfBirth(
      dateFormatters.dateAmerican.format(personalInfo.dateOfBirth),
    )
  }

  return proto
}

export function mapSpousalProfileVersionProtoToProps(
  proto: SpousalProfileProto,
): SpousalProfileVersionProps {
  const personalInfoProto = proto.getPersonalInfo()

  return {
    id: proto.getId(),
    version: proto.getVersion(),
    workspaceId: proto.getWorkspaceId(),
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    updatedAt: safeTimestampToDate(proto.getUpdateTime()),
    occupation: proto.getOccupation(),
    ownerUserId: proto.getOwnerUserId(),
    personalInfo: personalInfoProto
      ? mapPersonalInfoProtoToProps(personalInfoProto)
      : PersonalInfo.empty,
  }
}

export function mapDependentProfileVersionProtoToProps(
  proto: DependentProfileProto,
): DependentProfileVersionProps {
  const personalInfoProto = proto.getPersonalInfo()

  return {
    id: proto.getId(),
    version: proto.getVersion(),
    workspaceId: proto.getWorkspaceId(),
    createdAt: safeTimestampToDate(proto.getCreateTime()),
    updatedAt: safeTimestampToDate(proto.getUpdateTime()),
    relationship: proto.getRelationship(),
    ownerUserId: proto.getOwnerUserId(),
    personalInfo: personalInfoProto
      ? mapPersonalInfoProtoToProps(personalInfoProto)
      : PersonalInfo.empty,
  }
}
